/**
 * Estilos del widget, dentro de su Shadow DOM: el CSS del sitio no los toca y estos no tocan el sitio.
 * Solo px (los rem dependerían del tamaño de letra del sitio). Tema claro propio, legible sobre cualquier web.
 */
export const CSS = `
:host { all: initial; }
.anota, .anota * { box-sizing: border-box; }
.anota {
  --accent: #990f3d; --on-accent: #fff; --accent-text: #990f3d;
  --ease-out: cubic-bezier(.16,1,.3,1); --ease-in: cubic-bezier(.4,0,1,1);
  --ink: #1a1817; --muted: #6b6560; --line: #e2dcd6; --paper: #ffffff; --soft: #f6f2ee;
  font: 400 14px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
}
button, input, textarea { font: inherit; color: inherit; }
button { cursor: pointer; }
:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.launcher {
  position: fixed; bottom: 20px; right: 20px; display: inline-flex; align-items: center; gap: 8px;
  height: 44px; padding: 0 18px 0 14px; border: 0; border-radius: 999px; background: var(--accent); color: var(--on-accent);
  font-weight: 600; letter-spacing: .01em; box-shadow: 0 6px 20px rgba(0,0,0,.22), 0 1px 3px rgba(0,0,0,.2);
  transition: transform .15s ease, box-shadow .15s ease;
}
.launcher:hover { transform: translateY(-1px); box-shadow: 0 10px 26px rgba(0,0,0,.26), 0 2px 4px rgba(0,0,0,.2); }
.anota[data-position="bottom-left"] .launcher { right: auto; left: 20px; }
.launcher svg { flex: none; }
/* Botón redondo: solo el símbolo. */
.launcher.round { width: 48px; height: 48px; padding: 0; justify-content: center; }
.launcher.round svg { width: 16px; height: 22px; }
/* Pestaña lateral: asoma del borde y sale del todo al pasar el ratón o con el teclado. */
.tab-launch {
  position: fixed; top: 50%; right: 0; display: flex; flex-direction: column; align-items: center; gap: 8px;
  width: 34px; padding: 12px 0; border: 0; border-radius: 10px 0 0 10px; background: var(--accent); color: var(--on-accent);
  box-shadow: 0 4px 16px rgba(0,0,0,.2); transform: translate(6px, -50%); transition: transform .15s ease;
}
.tab-launch:hover, .tab-launch:focus-visible { transform: translate(0, -50%); }
.tab-launch span { writing-mode: vertical-rl; transform: rotate(180deg); font-weight: 600; font-size: 13px; letter-spacing: .02em; }
.anota[data-position="bottom-left"] .tab-launch { right: auto; left: 0; border-radius: 0 10px 10px 0; transform: translate(-6px, -50%); }
.anota[data-position="bottom-left"] .tab-launch:hover, .anota[data-position="bottom-left"] .tab-launch:focus-visible { transform: translate(0, -50%); }
/* Barra de revisión: lo más visible, para sesiones de revisión. Abajo, porque arriba tapaba el logo y el menú de la web.
   Se puede ocultar (queda la pestaña). */
.review-bar {
  position: fixed; bottom: 0; left: 0; right: 0; display: flex; align-items: center; justify-content: center; gap: 12px;
  min-height: 48px; padding: 6px 52px 6px 16px; padding-bottom: max(6px, env(safe-area-inset-bottom)); background: var(--accent); color: var(--on-accent); font-size: 14px;
  box-shadow: 0 -2px 12px rgba(0,0,0,.18);
}
.review-bar span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.review-bar svg { flex: none; }
.review-bar .rb-go { flex: none; height: 30px; padding: 0 14px; border: 0; border-radius: 999px; background: var(--on-accent); color: var(--accent); font-weight: 700; }
.review-bar .rb-x {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%); width: 32px; height: 32px;
  border: 0; border-radius: 8px; background: transparent; color: var(--on-accent); font-size: 15px;
}
.review-bar .rb-x:hover { background: rgba(255,255,255,.16); }
.review-bar .rb-go:focus-visible, .review-bar .rb-x:focus-visible { outline-color: var(--on-accent); }

/* La barra de elegir, abajo: arriba taparía el menú de la web, que suele estar ahí (y fijo, sin poder apartarlo). */
.bar {
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 6px;
  max-width: calc(100vw - 24px); padding: 6px 6px 6px 16px; border-radius: 999px; background: #1f1c1a; color: #fff;
  box-shadow: 0 8px 28px rgba(0,0,0,.3); white-space: nowrap;
}
.bar span { overflow: hidden; text-overflow: ellipsis; margin-right: 6px; }
.bar button { flex: none; height: 32px; padding: 0 12px; border: 1px solid rgba(255,255,255,.25); border-radius: 999px; background: transparent; color: #fff; }
.bar button:hover { background: rgba(255,255,255,.12); }

.hl {
  position: fixed; pointer-events: none; border: 2px solid var(--accent); border-radius: 4px;
  background: color-mix(in srgb, var(--accent) 12%, transparent);
  transition: top .09s var(--ease-out), left .09s var(--ease-out), width .09s var(--ease-out), height .09s var(--ease-out);
}
.hl.fixed { background: transparent; box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 22%, transparent); }
.pin {
  position: fixed; width: 22px; height: 22px; margin: -11px 0 0 -11px; pointer-events: none; box-sizing: border-box;
  border: 3px solid #fff; border-radius: 50%; background: var(--accent);
  box-shadow: 0 0 0 11px color-mix(in srgb, var(--accent) 25%, transparent), 0 2px 8px rgba(0,0,0,.35);
}
.hl-tag {
  position: absolute; left: -2px; bottom: 100%; margin-bottom: 4px; padding: 1px 6px; border-radius: 4px;
  background: var(--accent); color: var(--on-accent); font: 600 11px/1.5 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; white-space: nowrap;
}

.panel {
  position: fixed; bottom: 20px; right: 20px; width: 380px; max-width: calc(100vw - 24px); max-height: calc(100vh - 40px);
  overflow: auto; padding: 16px; border-radius: 14px; background: var(--paper); color: var(--ink);
  box-shadow: 0 18px 50px rgba(0,0,0,.28), 0 2px 6px rgba(0,0,0,.12);
}
.anota[data-position="bottom-left"] .panel { right: auto; left: 20px; }
/* Si el panel taparía lo señalado, se aparta: al otro lado o arriba (ver Form). */
.anota .panel.side-left { right: auto; left: 20px; }
.anota .panel.side-right { left: auto; right: 20px; }
.anota .panel.at-top { bottom: auto; top: 20px; }
.head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.head h2 { margin: 0; font-size: 15px; font-weight: 650; }
.x { display: grid; place-items: center; width: 30px; height: 30px; border: 0; border-radius: 8px; background: transparent; color: var(--muted); }
.x:hover { background: var(--soft); color: var(--ink); }
.ctx { margin: 4px 0 12px; color: var(--muted); font-size: 12.5px; overflow-wrap: anywhere; }
.ctx code { padding: 0 4px; border-radius: 4px; background: var(--soft); color: var(--ink); font: 12px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
textarea {
  display: block; width: 100%; min-height: 104px; max-height: 40vh; padding: 10px 12px; border: 1px solid var(--line);
  border-radius: 10px; background: var(--paper); resize: vertical;
}
textarea:focus, input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent); }
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0 0; padding: 0; border: 0; }
.chips legend { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
.chip { height: 28px; padding: 0 11px; border: 1px solid var(--line); border-radius: 999px; background: var(--paper); font-size: 12.5px; }
.chip:hover { border-color: var(--ink); }
.chip[aria-pressed="true"] { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
.who { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 10px; }
.who input { width: 100%; height: 36px; padding: 0 10px; border: 1px solid var(--line); border-radius: 8px; background: var(--paper); font-size: 13px; }
.check { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 13px; cursor: pointer; }
.check input { width: 16px; height: 16px; accent-color: var(--accent); }
.err { margin: 10px 0 0; color: #b42318; font-size: 13px; }
.actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 14px; }
.hint { color: var(--muted); font-size: 11.5px; }
.btns { display: flex; gap: 8px; }
.btn { height: 36px; padding: 0 16px; border: 1px solid var(--line); border-radius: 9px; background: var(--paper); font-weight: 600; }
.btn:hover { border-color: var(--ink); }
.btn.primary { border-color: var(--accent); background: var(--accent); color: var(--on-accent); }
.btn.primary:hover { filter: brightness(1.08); }
.btn[disabled] { opacity: .65; cursor: progress; }
/* ── Área dibujada al elegir (con ratón) ── */
.area-box { position: fixed; pointer-events: none; border: 2px dashed var(--accent); border-radius: 4px; background: color-mix(in srgb, var(--accent) 10%, transparent); }
.area-box.fixed { border-style: solid; background: transparent; box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 22%, transparent); }
.shot-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 6px 12px; }

/* ── Editor de marcas sobre la captura ── */
.draw { position: fixed; inset: 0; z-index: 10; display: flex; flex-direction: column; background: rgba(20,18,17,.86); }
.draw-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 10px 14px; background: var(--paper); box-shadow: 0 2px 12px rgba(0,0,0,.25); }
.draw-title { margin-right: auto; font-weight: 700; }
.draw-tools, .draw-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.draw-tools button { height: 32px; padding: 0 12px; border: 1px solid var(--line); border-radius: 999px; background: var(--paper); }
.draw-tools button[aria-pressed="true"] { border-color: var(--accent); background: var(--accent); color: var(--on-accent); }
.draw-tools button:disabled { opacity: .5; cursor: default; }
.draw-stage { flex: 1; min-height: 0; display: grid; place-items: center; padding: 16px; overflow: auto; }
.draw-stage canvas { max-width: 100%; max-height: calc(100vh - 96px); max-height: calc(100dvh - 96px); border-radius: 6px; background: #fff; box-shadow: 0 10px 40px rgba(0,0,0,.4); cursor: crosshair; touch-action: none; }
@media (max-width: 520px) { .draw-title { flex: 1 1 100%; } .draw-stage { padding: 8px; } .draw-stage canvas { max-height: calc(100dvh - 160px); } }

/* ── Pines: las notas de la página para quien la revisa ── */
.anota { --s-open: var(--accent); --s-progress: #b26a00; --s-done: #23753d; --s-closed: #6b6560; }
.s-open { --c: var(--s-open); } .s-in_progress { --c: var(--s-progress); } .s-resolved { --c: var(--s-done); }
.s-wont_fix, .s-duplicate { --c: var(--s-closed); }
.npin {
  position: fixed; width: 26px; height: 26px; margin: -13px 0 0 -13px; display: grid; place-items: center; padding: 0;
  border: 2px solid #fff; border-radius: 50%; background: var(--c); color: #fff; font-size: 12px; font-weight: 700; line-height: 1;
  box-shadow: 0 2px 8px rgba(0,0,0,.3);
}
.npin.on, .npin:hover { box-shadow: 0 0 0 5px color-mix(in srgb, var(--c) 30%, transparent), 0 2px 8px rgba(0,0,0,.3); }
.notes-chip {
  position: fixed; bottom: 20px; left: 20px; height: 36px; padding: 0 14px; border: 1px solid var(--line); border-radius: 999px;
  background: var(--paper); color: var(--ink); font-weight: 600; box-shadow: 0 4px 14px rgba(0,0,0,.16);
}
.anota[data-position="bottom-left"] .notes-chip { left: auto; right: 20px; }
.notes-list, .npop {
  position: fixed; width: 320px; max-width: calc(100vw - 16px); max-height: 60vh; overflow: auto; padding: 12px;
  border-radius: 12px; background: var(--paper); color: var(--ink); box-shadow: 0 16px 44px rgba(0,0,0,.26), 0 2px 6px rgba(0,0,0,.1);
}
.notes-list { bottom: 64px; left: 20px; }
.anota[data-position="bottom-left"] .notes-list { left: auto; right: 20px; }
.nl-head, .np-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.nl-head b { flex: 1; }
.notes-list ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 4px; }
.notes-list li button {
  display: grid; grid-template-columns: auto auto 1fr; align-items: baseline; gap: 2px 8px; width: 100%; padding: 8px;
  border: 0; border-radius: 8px; background: transparent; text-align: left;
}
.notes-list li button:hover { background: var(--soft); }
.nl-num { font-weight: 700; }
.nl-desc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nl-where { grid-column: 1 / -1; color: var(--muted); font-size: 12px; }
.link { padding: 0; border: 0; background: none; color: var(--accent-text); font-size: 13px; text-decoration: underline; }
.np-status {
  display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 999px; font-size: 11.5px; font-weight: 700;
  background: color-mix(in srgb, var(--c) 14%, transparent); color: var(--c); white-space: nowrap;
}
.np-head b { flex: 1; }
.npop.docked { right: 20px; bottom: 64px; }
.np-desc { margin: 0 0 8px; white-space: pre-wrap; overflow-wrap: anywhere; }
.np-meta { margin: 0 0 8px; color: var(--muted); font-size: 12.5px; }
.np-done { margin: 0 0 10px; padding: 8px 10px; border-radius: 10px; background: color-mix(in srgb, var(--s-done) 10%, transparent); font-size: 13px; }
/* «¿Está bien así?» bajo lo que se hizo: dos botones; «No, sigue mal» abre el cuadro para decir qué. */
.np-verdict { display: grid; gap: 8px; margin: 0 0 12px; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; font-size: 13px; }
.np-verdict form { display: grid; gap: 8px; }
.np-verdict textarea { width: 100%; padding: 8px 10px; border: 1px solid var(--line); border-radius: 8px; background: var(--paper); color: var(--ink); font: inherit; resize: vertical; }
.np-verdict-row { display: flex; flex-wrap: wrap; gap: 8px; }
.np-verdict .btn { height: 32px; padding: 0 12px; font-size: 13px; }
.np-verdict .err { margin: 0; }
.np-ok { margin: 0 0 10px; color: var(--s-done); font-size: 13px; font-weight: 600; }
/* La conversación, como un chat: lo del cliente a la derecha; lo del equipo y los agentes, a la izquierda. */
.np-thread { display: flex; flex-direction: column; gap: 8px; margin: 0 0 10px; padding: 0; list-style: none; }
.np-thread li { align-self: flex-start; max-width: 88%; padding: 7px 11px 8px; border-radius: 14px 14px 14px 4px; background: var(--soft); font-size: 13px; }
.np-thread li.by-reporter { align-self: flex-end; border-radius: 14px 14px 4px 14px; background: color-mix(in srgb, var(--accent) 10%, var(--paper)); }
.np-who { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0 6px; margin-bottom: 1px; color: var(--muted); font-size: 11.5px; }
.np-who b { color: var(--ink); font-weight: 650; }
.np-tag { padding: 0 6px; border: 1px solid var(--line); border-radius: 999px; background: var(--paper); font-size: 10.5px; font-weight: 600; line-height: 16px; }
.np-body { display: block; white-space: pre-wrap; overflow-wrap: anywhere; }
/* Responder: el cuadro crece con el texto; enviar, un botón redondo. */
.np-reply { display: grid; grid-template-columns: 1fr auto; align-items: end; gap: 8px; }
.np-reply textarea { width: 100%; min-height: 40px; max-height: 140px; padding: 9px 14px; border: 1px solid var(--line); border-radius: 20px; line-height: 1.4; resize: none; }
.np-reply .send { display: grid; place-items: center; width: 40px; height: 40px; padding: 0; border: 0; border-radius: 50%; background: var(--accent); color: var(--on-accent); }
.np-reply .send:hover { filter: brightness(1.08); }
.np-reply .send:disabled { background: var(--line); color: var(--muted); cursor: default; filter: none; }
.np-reply .err { grid-column: 1 / -1; margin: 0; }
@media (max-width: 520px) {
  .npop, .npop.docked, .notes-list { left: 8px !important; right: 8px; top: auto !important; bottom: 8px; width: auto; max-width: none; }
  .notes-chip { bottom: 12px; left: 12px; }
}

.toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); max-width: calc(100vw - 24px);
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px 12px;
  padding: 10px 16px; border-radius: 22px; background: #1f1c1a; color: #fff; box-shadow: 0 8px 28px rgba(0,0,0,.3);
}
.toast .link { color: #fff; font-weight: 600; }

/* ── «Tus notas»: el historial de quien anota ── */
/* Cabecera fija; el contenido se desplaza; el cuadro de responder, siempre al pie. */
.panel.mine { display: flex; flex-direction: column; max-height: min(640px, calc(100vh - 40px)); padding: 0; overflow: hidden; }
.mine-head { display: flex; align-items: center; gap: 8px; padding: 12px 10px 12px 16px; border-bottom: 1px solid var(--line); }
.mine-title { flex: 1; min-width: 0; }
.mine-title h2 { margin: 0; font-size: 15px; font-weight: 650; }
.mine-title span { display: block; overflow: hidden; color: var(--muted); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.mine-head .back { flex: 1; display: flex; align-items: center; gap: 6px; height: 30px; padding: 0; border: 0; background: none; color: var(--accent-text); font-weight: 600; text-align: left; }
.mine-body { flex: 1; min-height: 0; padding: 12px 16px; overflow: auto; overscroll-behavior: contain; }
.mine a { color: var(--accent-text); overflow-wrap: anywhere; }
.mine-filter { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }
.mine-filter button { height: 28px; padding: 0 10px; border: 1px solid var(--line); border-radius: 999px; background: var(--paper); font-size: 12.5px; }
.mine-filter button span { color: var(--muted); }
.mine-filter button[aria-pressed="true"] { border-color: var(--ink); background: var(--ink); color: #fff; }
.mine-filter button[aria-pressed="true"] span { color: rgba(255,255,255,.7); }
.mine-list { margin: 0 -8px; padding: 0; list-style: none; }
.mine-list li + li { border-top: 1px solid var(--line); }
.mine-list button { display: block; width: 100%; padding: 10px 8px; border: 0; border-radius: 8px; background: transparent; text-align: left; }
.mine-list button:hover { background: var(--soft); }
.mi-top { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.mi-num { font-size: 13px; font-weight: 700; }
.mi-when { margin-left: auto; color: var(--muted); font-size: 12px; white-space: nowrap; }
.mi-desc { display: -webkit-box; overflow: hidden; overflow-wrap: anywhere; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.mine-list .new .mi-desc { font-weight: 600; }
.mi-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 4px; color: var(--muted); font-size: 12px; }
.mi-replies { display: inline-flex; align-items: center; gap: 4px; }
.nl-new { padding: 0 7px; border-radius: 999px; background: var(--accent); color: var(--on-accent); font-size: 11px; font-weight: 700; line-height: 18px; }
.mine-skel i {
  display: block; height: 58px; margin: 4px 0 10px; border-radius: 10px;
  background: linear-gradient(90deg, var(--soft) 25%, #fcfaf8 50%, var(--soft) 75%); background-size: 200% 100%; animation: anota-shimmer 1.2s linear infinite;
}
.mine-state { display: grid; justify-items: center; gap: 6px; padding: 18px 8px 22px; text-align: center; }
/* Al pie de la lista: el enlace a sus notas para otro dispositivo. */
.mine-device { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line); color: var(--muted); font-size: 12.5px; }
.mine-device p, .mine-device label { flex-basis: 100%; margin: 0; color: var(--ink); }
.mine-device input { display: block; width: 100%; margin-top: 4px; padding: 6px 8px; border: 1px solid var(--line); border-radius: 7px; background: var(--paper); color: var(--ink); font: 12px ui-monospace, Menlo, Consolas, monospace; }
.mine-state p { margin: 0; color: var(--muted); font-size: 13px; }
.mine-icon { display: grid; place-items: center; width: 44px; height: 44px; margin-bottom: 4px; border-radius: 50%; background: color-mix(in srgb, var(--accent) 10%, transparent); color: var(--accent-text); }
.sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
/* Una nota: título, en qué punto está, dónde, y la conversación. */
.md-title { display: flex; align-items: baseline; gap: 8px; margin-bottom: 12px; }
.md-title h3 { margin: 0; font-size: 16px; font-weight: 700; }
.md-type { padding: 0 8px; border-radius: 999px; background: var(--soft); color: var(--muted); font-size: 11.5px; font-weight: 600; line-height: 20px; }
.md-title time { margin-left: auto; color: var(--muted); font-size: 12px; }
.steps { display: flex; margin: 0; padding: 0; list-style: none; }
.steps li { position: relative; flex: 1; padding-top: 18px; color: var(--muted); font-size: 11.5px; text-align: center; }
.steps li::before {
  content: ""; position: absolute; top: 2px; left: 50%; z-index: 1; width: 12px; height: 12px; margin-left: -6px; border-radius: 50%;
  background: var(--paper); box-shadow: inset 0 0 0 2px var(--line);
}
.steps li::after { content: ""; position: absolute; top: 7px; right: 50%; left: -50%; height: 2px; background: var(--line); }
.steps li:first-child::after { display: none; }
.steps li.on { color: var(--ink); font-weight: 600; }
.steps li.on::before { background: var(--c); box-shadow: 0 0 0 3px color-mix(in srgb, var(--c) 20%, transparent); }
.steps li.on::after { background: var(--c); }
.md-hint { margin: 10px 0 12px; padding: 8px 10px; border-radius: 10px; background: color-mix(in srgb, var(--c) 9%, transparent); font-size: 13px; }
.md-page { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin: 0 0 12px; color: var(--muted); font-size: 12.5px; }
.md-page .where {
  display: inline-flex; align-items: center; gap: 5px; height: 26px; padding: 0 10px; border: 1px solid var(--line); border-radius: 999px;
  background: var(--paper); color: var(--ink); font-size: 12px; font-weight: 600;
}
.md-page .where:hover { border-color: var(--accent); color: var(--accent-text); }
.mine .np-desc { margin: 0 0 8px; padding: 2px 0 2px 12px; border-left: 3px solid var(--line); }
.mine .md-page { margin-bottom: 14px; }
.mine .np-reply { position: sticky; bottom: -12px; margin: 14px -16px -12px; padding: 10px 16px 12px; border-top: 1px solid var(--line); background: var(--paper); }
@keyframes anota-shimmer { to { background-position: -200% 0; } }
@keyframes anota-in { from { opacity: 0; transform: translateY(8px); } }
.panel, .npop, .notes-list, .news-chip { animation: anota-in .22s var(--ease-out); }
/* Novedades: junto al botón (encima de él; con la pestaña o la barra, abajo del todo). */
.news-chip {
  position: fixed; right: 20px; bottom: 80px; display: flex; align-items: center; height: 36px; padding: 0 4px 0 0;
  border: 1px solid var(--line); border-radius: 999px; background: var(--paper); color: var(--ink); font-weight: 600;
  box-shadow: 0 6px 20px rgba(0,0,0,.18);
}
.news-chip:hover { border-color: var(--accent); }
.news-chip button { border: 0; background: none; color: inherit; font: inherit; cursor: pointer; }
.news-chip .news-open { display: flex; align-items: center; gap: 8px; height: 100%; padding: 0 8px 0 14px; border-radius: 999px 0 0 999px; }
.news-chip .news-x { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; color: var(--muted); font-size: 18px; line-height: 1; }
.news-chip .news-x:hover { background: var(--soft); color: var(--ink); }
.news-chip button:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.news-chip .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); }
.anota[data-position="bottom-left"] .news-chip { right: auto; left: 20px; }
.anota[data-launcher="tab"] .news-chip, .anota[data-launcher="bar"] .news-chip { bottom: 20px; }
.anota[data-bar] .news-chip, .anota[data-bar] .notes-chip, .anota[data-bar] .toast { bottom: 68px; }
.anota[data-bar] .notes-list, .anota[data-bar] .npop.docked { bottom: 112px; }
.bar .count { display: inline-grid; place-items: center; min-width: 18px; height: 18px; margin-left: 6px; padding: 0 5px; border-radius: 999px; background: #fff; color: #1f1c1a; font-size: 11px; font-weight: 700; }

@media (max-width: 520px) {
  .panel { left: 8px; right: 8px; bottom: 8px; width: auto; max-width: none; border-radius: 16px; }
  .anota[data-position="bottom-left"] .panel { left: 8px; }
  .anota .panel.side-left, .anota .panel.side-right { left: 8px; right: 8px; }
  .anota .panel.at-top { top: 8px; }
  .hint { display: none; }
  /* Encima del botón de Anotar, no sobre él. */
  .toast { bottom: 84px; }
  .panel.mine { max-height: 80vh; max-height: 80dvh; }
  /* Tres botones no caben junto al texto: el texto arriba y la barra a lo ancho. */
  .bar { bottom: 8px; left: 8px; right: 8px; transform: none; max-width: none; flex-wrap: wrap; justify-content: center; padding: 8px; border-radius: 18px; white-space: normal; }
  .bar span { flex: 1 1 100%; margin: 0 0 2px; text-align: center; }
}
@media (prefers-reduced-motion: reduce) {
  .launcher, .hl { transition: none; }
  .panel, .npop, .notes-list, .news-chip, .mine-skel i { animation: none; }
}

/* Lo que abre el widget, siempre por encima de los pines (un pin no puede tapar el botón). */
.launcher, .tab-launch, .review-bar, .news-chip { z-index: 2; }
/* Y lo que se abre (formulario, notas, avisos), por encima de todo lo demás. */
.panel, .npop, .notes-list, .toast { z-index: 3; }
/* Pin de un estado abierto: su texto sobre el color de marca. */
.npin.s-open { color: var(--on-accent); }

/* Pantallas táctiles: objetivos de 44 px y campos de 16 px (si no, iOS amplía la página al tocarlos). */
@media (pointer: coarse) {
  textarea, input, .who input, .np-reply textarea { font-size: 16px; }
  .x, .review-bar .rb-x { width: 44px; height: 44px; }
  .btn, .bar button, .review-bar .rb-go, .draw-tools button { min-height: 44px; }
  .chip, .mine-filter button { min-height: 40px; }
  .link, .md-page .where { display: inline-flex; align-items: center; min-height: 40px; }
  .npin { width: 32px; height: 32px; margin: -16px 0 0 -16px; font-size: 13px; }
  .hint { display: none; }
  .tab-launch { top: 72%; }
}
/* Pantallas bajas (móvil en horizontal, zoom): el panel más compacto y los botones de enviar siempre a la vista. */
@media (max-height: 560px) {
  textarea { min-height: 64px; }
  .ctx { margin-bottom: 8px; }
  .actions { position: sticky; bottom: -16px; margin: 12px -16px -16px; padding: 10px 16px 16px; border-top: 1px solid var(--line); background: var(--paper); }
}
@media (max-width: 360px) {
  textarea { min-height: 80px; }
}
@media (max-width: 520px) {
  .review-bar { gap: 8px; padding-left: 12px; font-size: 13px; }
  .review-bar > svg { display: none; }
}

/* ── Movimiento ── */
@keyframes anota-fade { from { opacity: 0; } }
@keyframes anota-out { to { opacity: 0; transform: translateY(6px); } }
@keyframes anota-fade-out { to { opacity: 0; } }
@keyframes anota-draw { to { stroke-dashoffset: 0; } }
@keyframes anota-ping { from { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent); } to { box-shadow: 0 0 0 9px transparent; } }
/* El botón aparece con un fundido (también al cerrar el formulario); solo opacidad: no se mueve de su sitio. */
.launcher, .tab-launch, .review-bar { animation: anota-fade .3s var(--ease-out) .08s backwards; }
/* Al pulsar, cede un poco. */
.launcher:active { transform: translateY(0) scale(.97); }
.btn, .chip, .bar button, .x, .mine-filter button, .draw-tools button, .np-reply .send, .md-page .where, .rb-go {
  transition: background-color .12s ease, border-color .12s ease, color .12s ease, transform .12s var(--ease-out), filter .12s ease;
}
.btn:active:not([disabled]), .chip:active, .bar button:active, .x:active, .mine-filter button:active, .draw-tools button:active, .np-reply .send:active:not(:disabled), .rb-go:active { transform: scale(.96); }
/* Al cerrarse: el formulario y los paneles bajan y se desvanecen; la barra de elegir, solo se desvanece. */
.anota[data-leaving] .panel { animation: anota-out .14s var(--ease-in) forwards; }
.anota[data-leaving] .bar, .anota[data-leaving] .hl, .anota[data-leaving] .pin, .anota[data-leaving] .area-box { animation: anota-fade-out .14s var(--ease-in) forwards; }
/* Los pines, escalonados (solo opacidad). */
.npin { animation: anota-fade .3s var(--ease-out) backwards; animation-delay: calc(var(--i, 0) * 45ms); }
/* «¡Gracias!»: el check se dibuja; el aviso se va desvaneciéndose. */
.toast .tick { flex: none; fill: none; stroke: #7ee2a0; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.toast .tick path { stroke-dasharray: 16; stroke-dashoffset: 16; animation: anota-draw .45s var(--ease-out) .12s forwards; }
.toast { animation: anota-fade .2s var(--ease-out); }
.toast.out { animation: anota-fade-out .16s var(--ease-in) forwards; }
/* Novedades: el punto late dos veces para llamar la atención, sin insistir. */
.news-chip .dot { animation: anota-ping 1.4s var(--ease-out) .4s 2; }
/* Quien pidió menos movimiento: todo llega a su estado final al momento. */
@media (prefers-reduced-motion: reduce) {
  .anota *, .anota *::before, .anota *::after { animation-duration: 1ms !important; animation-delay: 0ms !important; animation-iteration-count: 1 !important; transition-duration: 1ms !important; }
}
`;
